import type { SprintRoute } from '@waago/api'

/**
 * Which sprint delivers each screen of the provider app (Waago Pro).
 *
 * <p>Derived from `WaaGo_Matrice_Fonctionnalites_Sprints.xlsx`. Every AF ref
 * in the matrix lands at Sprint 2 or later — M3 (AF Onboarding & KYC) is the
 * earliest milestone that touches this app at all, so there is no Sprint 1
 * screen here and a Sprint-1 build shows nothing but locked screens behind
 * the tab bar. That is not a bug in the mapping: it is what "Waago Pro has
 * 3/86 frames" in the roadmap's own M3 description means in sprint terms.
 *
 * <p>Judgement calls, since a few routes have no exact matrix row:
 *
 * - **`/employes` and `/employes/ajouter` are Sprint 2** — staff management
 *   has no dedicated AF row; grouped with the other M4 operational screens.
 * - **`/publicite` and `/aide/signalement` are Sprint 3 / Sprint 4
 *   respectively** — advertising mirrors BO-019 (M7, Sprint 3); the report
 *   form mirrors the client app's own signalement, which M8's text covers
 *   without a dedicated ref (Sprint 4).
 * - **`/missions/:id` is Sprint 2** even though GPS navigation and status
 *   transitions inside it are AF-015/016 (M5, Sprint 3) — the screen's
 *   accept/decline and delivery-proof pieces (AF-013/014/017) are M4, so it
 *   opens at the earlier sprint; the M5 sub-features are a component-level
 *   call, not a routing one.
 */
export const PROVIDER_SPRINTS: SprintRoute[] = [
  // --- Sprint 2 · M3 AF Onboarding & KYC, M4 AF Operations ---------------
  { path: '/bienvenue', sprint: 2 }, // AF-001 Splash Waago Pro
  { path: '/connexion', sprint: 2 }, // AF-002 Connexion + OTP
  { path: '/inscription', sprint: 2 }, // AF-003..007 Onboarding
  { path: '/compte', sprint: 2 }, // AF-026 Profil prestataire
  { path: '/compte/profil', sprint: 2 },
  { path: '/compte/securite', sprint: 2 }, // AF-030 Paramètres
  { path: '/compte/securite/numero', sprint: 2 },
  { path: '/compte/langue', sprint: 2 },
  { path: '/compte/permission', sprint: 2 },
  { path: '/compte/documents', sprint: 2 }, // AF-027 Gestion documents
  { path: '/', sprint: 2 }, // AF-008/009/010 Dashboard
  { path: '/offres', sprint: 2 }, // AF-012 Liste missions
  { path: '/offres/:id', sprint: 2 }, // AF-013/014 Détail, accepter/refuser
  { path: '/missions', sprint: 2 },
  { path: '/missions/:id', sprint: 2 }, // AF-013/014/017 (AF-015/016 gated in-component)
  { path: '/services', sprint: 2 }, // AF-018/019/021 Config véhicules/tarifs/dispo
  { path: '/services/nouveau', sprint: 2 },
  { path: '/services/:id', sprint: 2 },
  { path: '/vehicules', sprint: 2 },
  { path: '/employes', sprint: 2 },
  { path: '/employes/ajouter', sprint: 2 },

  // --- Sprint 3 · M5 Tracking, M6 Money, M7 Back-Office -------------------
  { path: '/revenus', sprint: 3 }, // AF-023 Retrait Mobile Money
  { path: '/revenus/virements', sprint: 3 },
  { path: '/compte/paiement', sprint: 3 }, // AF-024 Commissions
  { path: '/abonnement', sprint: 3 }, // AF-025 Gestion abonnement
  { path: '/abonnement/:code', sprint: 3 },
  { path: '/publicite', sprint: 3 },
  { path: '/publicite/:id', sprint: 3 },

  // --- Sprint 4 · M8 Notifications & Support ------------------------------
  { path: '/notifications', sprint: 4 }, // AF-028
  { path: '/aide', sprint: 4 }, // AF-029 Aide & Support
  { path: '/aide/contact', sprint: 4 },
  { path: '/aide/signalement', sprint: 4 },
]
